// src/components/Navbar.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Link as ScrollLink } from "react-scroll";
import { FaBars, FaTimes } from "react-icons/fa";
import logo from "../assets/logo.png"; // You'll need to create/add this

const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 30px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease-in-out;
  background: ${({ scrolled }) => (scrolled ? 'rgba(10, 10, 10, 0.95)' : 'transparent')};
  backdrop-filter: ${({ scrolled }) => (scrolled ? 'blur(10px)' : 'none')};
  box-shadow: ${({ scrolled }) => (scrolled ? '0 2px 10px rgba(0, 0, 0, 0.3)' : 'none')};

  @media (max-width: 768px) {
    padding: 10px 20px;
  }
`;

const Logo = styled.div`
  img {
    height: 100px;
    transition: all 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.3);
  }
`;

const MenuToggle = styled.div`
  display: none;
  cursor: pointer;
  font-size: 24px;
  color: #fff;
  z-index: 1001;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const NavItems = styled(motion.div)`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    position: fixed;
    flex-direction: column;
    justify-content: center;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100%;
    background: rgba(0, 0, 0, 0.95);
    padding: 2rem;
    backdrop-filter: blur(10px);
    z-index: 1000;
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  }
`;

const NavItem = styled(ScrollLink)`
  margin: 0 15px;
  cursor: pointer;
  position: relative;
  font-family: 'Orbitron', sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
  transition: all 0.3s ease;
  color: #fff;
  text-decoration: none;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -5px;
    left: 0;
    background: linear-gradient(45deg, #ff9900, #ff6600);
    transition: width 0.3s;
  }
  
  &:hover {
    color: #ff9900;
    &:after {
      width: 100%;
    }
  }
  
  &.active {
    color: #ff9900;
    &:after {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    margin: 15px 0;
    font-size: 24px;
  }
`;



const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuVariants = {
    open: {
      opacity: 1,
      x: 0,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    },
    closed: {
      opacity: 0,
      x: "-100%",
      transition: { staggerChildren: 0.05, staggerDirection: -1 }
    }
  };

  const itemVariants = {
    open: { opacity: 1, y: 0 },
    closed: { opacity: 0, y: 20 }
  };

  return (
    <NavContainer scrolled={scrolled}>
      <Logo>
        <img src={logo} alt="Hopito Logo" />
      </Logo>

      <MenuToggle onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <FaTimes /> : <FaBars />}
      </MenuToggle>

      <AnimatePresence>
        {(isOpen || window.innerWidth > 768) && (
          <NavItems
            isOpen={isOpen}
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
          >
            <NavItem
              to="home"
              smooth={true}
              duration={500}
              spy={true}
              offset={-70}
              activeClass="active"
              onClick={() => setIsOpen(false)}
              variants={itemVariants}
              as={motion.div}
            >
              Home
            </NavItem>
            <NavItem
              to="activities"
              smooth={true}
              duration={500}
              spy={true}
              offset={-70}
              activeClass="active"
              onClick={() => setIsOpen(false)}
              variants={itemVariants}
              as={motion.div}
            >
              Activities
            </NavItem>
            <NavItem
              to="attractions"
              smooth={true}
              duration={500}
              spy={true}
              offset={-70}
              activeClass="active"
              onClick={() => setIsOpen(false)}
              variants={itemVariants}
              as={motion.div}
            >
              Attractions
            </NavItem>
            <NavItem
              to="gallery"
              smooth={true}
              duration={500}
              spy={true}
              offset={-70}
              activeClass="active"
              onClick={() => setIsOpen(false)}
              variants={itemVariants}
              as={motion.div}
            >
              Gallery
            </NavItem>
            {/* <NavItem 
              to="pricing" 
              smooth={true} 
              duration={500} 
              spy={true} 
              offset={-70}
              activeClass="active"
              onClick={() => setIsOpen(false)}
              variants={itemVariants}
              as={motion.div}
            >
              Pricing
            </NavItem> */}
            <NavItem
              to="contact"
              smooth={true}
              duration={500}
              spy={true}
              offset={-70}
              activeClass="active"
              onClick={() => setIsOpen(false)}
              variants={itemVariants}
              as={motion.div}
            >
              Contact
            </NavItem>

            {/* <NavButton 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              variants={itemVariants}
            >
              Book Now
            </NavButton> */}
          </NavItems>
        )}
      </AnimatePresence>
    </NavContainer>
  );
};

export default Navbar;